import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';

import { useCheckCondition } from '../../hooks/useCheckCondition';

import { After, Comment, Label } from '../../types/global';
import { useAppContext } from '../../context/appContext';
import { useQuestion } from '../../hooks/useQuestion';
import { cn } from '@/lib/utils';
import { Edge } from '../../types/kb';

const Edges = ({ edges }: { edges: Edge<Label | Comment | After>[] }) => {
  const checkCondition = useCheckCondition();

  return (
    <div className="flex flex-col gap-4">
      {edges.map((edge) => (
        <div
          key={edge.node.id}
          className={cn(
            'flex flex-col gap-2 p-2 rounded',
            checkCondition(edge.properties?.condition)
              ? 'bg-emerald-900/30 border border-emerald-700'
              : '',
          )}
        >
          <p className="font-medium text-[var(--dbg-text)]">
            {edge.node.value}
          </p>
          {edge.properties?.condition && (
            <code className="bg-[var(--dbg-bg-code)] text-emerald-300 px-2 py-1 rounded text-xs font-mono">
              {edge.properties.condition}
            </code>
          )}
        </div>
      ))}
    </div>
  );
};

export const Question = () => {
  const { debugQuestion, setDebugQuestion } = useAppContext();
  const { data } = useQuestion(debugQuestion);
  const question = data?.questions?.[0];

  return (
    <div className="flex flex-col bg-[var(--dbg-bg)] p-2 gap-2">
      <input
        defaultValue={debugQuestion || ''}
        onBlur={(e) => setDebugQuestion(e.target.value)}
        placeholder="Question ID"
        className="w-full bg-[var(--dbg-bg-secondary)] border border-[var(--dbg-border-input)] text-[var(--dbg-text)] text-sm font-mono rounded px-3 py-2 placeholder:text-[var(--dbg-text-dim)] focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
      />
      <div className="flex flex-col">
        <AccordionItem
          defaultOpen
          className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-none"
        >
          <AccordionTrigger className="px-4 text-[var(--dbg-text)] [&>svg]:text-[var(--dbg-text-secondary)]">
            Labels
          </AccordionTrigger>
          <AccordionContent className="px-4">
            <Edges edges={question?.labelsConnection?.edges ?? []} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          defaultOpen
          className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-none"
        >
          <AccordionTrigger className="px-4 text-[var(--dbg-text)] [&>svg]:text-[var(--dbg-text-secondary)]">
            After
          </AccordionTrigger>
          <AccordionContent className="px-4">
            <Edges
              edges={
                question?.afterConnection?.edges.map((e) => ({
                  ...e,
                  node: { id: e.node.extId, value: e.node.extId },
                })) ?? []
              }
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          defaultOpen
          className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-none"
        >
          <AccordionTrigger className="px-4 text-[var(--dbg-text)] [&>svg]:text-[var(--dbg-text-secondary)]">
            Comments
          </AccordionTrigger>
          <AccordionContent className="px-4">
            <Edges edges={question?.commentsConnection?.edges ?? []} />
          </AccordionContent>
        </AccordionItem>
      </div>
    </div>
  );
};
